/**
 * Local Bridge: view and manage company transactions (Revenue, Expense, Sales).
 * Transactions have specific dates; aggregator sums by period.
 * Sales create revenue + CAC + COGS entries.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Card,
  Form,
  Input,
  InputNumber,
  Button,
  Table,
  Tabs,
  message,
  Spin,
  Alert,
  Modal,
  Select,
  Space,
  Typography,
  DatePicker,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { ArrowLeftOutlined, EditOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { isLocalBridgeApiEnabled } from '../services/bridgeSessionDataAdapter';
import {
  getCompany,
  listCompanyTransactions,
  createCompanyTransaction,
  updateCompanyTransaction,
  deleteCompanyTransaction,
  listCompanySales,
  createCompanySale,
  deleteCompanySale,
  listMarketFitProducts,
  type LocalCompany,
  type CompanyTransactionItem,
  type CompanySaleItem,
  type MarketFitProduct,
} from '../services/localBridgeSessionApi';

const { Text } = Typography;

const EXPENSE_CATEGORIES = [
  { value: 'COGS', label: 'COGS' },
  { value: 'CAC', label: 'CAC' },
  { value: 'Operations', label: 'Operations' },
  { value: 'Salaries', label: 'Salaries' },
  { value: 'Marketing', label: 'Marketing' },
  { value: 'R&D', label: 'R&D' },
  { value: 'Other', label: 'Other' },
];

const EXPENSE_TYPES = [
  { value: 'fixed', label: 'Fixed' },
  { value: 'variable', label: 'Variable' },
  { value: 'one-time', label: 'One-time' },
];

type TabKey = 'revenue' | 'expense' | 'sales';

export const LocalBridgeCompanyTransactionsPage: React.FC = () => {
  const { companyId } = useParams<{ companyId: string }>();
  const [company, setCompany] = useState<LocalCompany | null>(null);
  const [revenueItems, setRevenueItems] = useState<CompanyTransactionItem[]>([]);
  const [expenseItems, setExpenseItems] = useState<CompanyTransactionItem[]>([]);
  const [salesItems, setSalesItems] = useState<CompanySaleItem[]>([]);
  const [products, setProducts] = useState<MarketFitProduct[]>([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<TabKey>('revenue');
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [saleModalOpen, setSaleModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<CompanyTransactionItem | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [periodFilter, setPeriodFilter] = useState<string>('');
  const [addForm] = Form.useForm();
  const [editForm] = Form.useForm();
  const [saleForm] = Form.useForm();

  const enabled = isLocalBridgeApiEnabled();
  const cid = companyId ?? '';

  const loadCompany = useCallback(async () => {
    if (!enabled || !cid) return;
    const c = await getCompany(cid);
    setCompany(c ?? null);
  }, [enabled, cid]);

  const loadProducts = useCallback(async () => {
    if (!enabled || !cid) return;
    const list = await listMarketFitProducts(cid);
    setProducts(list ?? []);
  }, [enabled, cid]);

  const loadTransactions = useCallback(async () => {
    if (!enabled || !cid) return;
    setLoading(true);
    try {
      const params = periodFilter ? { period_id: periodFilter } : undefined;
      const [rev, exp, sales] = await Promise.all([
        listCompanyTransactions(cid, { transaction_type: 'revenue', ...params }),
        listCompanyTransactions(cid, { transaction_type: 'expense', ...params }),
        listCompanySales(cid, params),
      ]);
      setRevenueItems(rev);
      setExpenseItems(exp);
      setSalesItems(sales);
    } catch {
      setRevenueItems([]);
      setExpenseItems([]);
      setSalesItems([]);
    } finally {
      setLoading(false);
    }
  }, [enabled, cid, periodFilter]);

  useEffect(() => {
    loadCompany();
    loadProducts();
  }, [loadCompany, loadProducts]);

  useEffect(() => {
    loadTransactions();
  }, [loadTransactions]);

  const openAddModal = () => {
    addForm.setFieldsValue({
      transaction_date: dayjs(),
      amount: 0,
      data_type: 'actual',
      product_id: undefined,
      expense_category: undefined,
      expense_type: undefined,
    });
    setAddModalOpen(true);
  };

  const handleAddSubmit = async () => {
    try {
      const values = await addForm.validateFields();
      setSubmitting(true);
      const created = await createCompanyTransaction(cid, {
        transaction_type: activeTab === 'revenue' ? 'revenue' : 'expense',
        transaction_date: typeof values.transaction_date === 'string' ? values.transaction_date : values.transaction_date?.format?.('YYYY-MM-DD') ?? dayjs().format('YYYY-MM-DD'),
        amount: values.amount,
        product_id: activeTab === 'revenue' ? values.product_id : undefined,
        expense_category: activeTab === 'expense' ? values.expense_category : undefined,
        expense_type: activeTab === 'expense' ? values.expense_type : undefined,
        data_type: values.data_type,
      });
      if (created) {
        message.success('Transaction added');
        setAddModalOpen(false);
        addForm.resetFields();
        loadTransactions();
      } else {
        message.error('Failed to add transaction');
      }
    } catch (e) {
      if (e && typeof e === 'object' && 'errorFields' in e) return;
    } finally {
      setSubmitting(false);
    }
  };

  const openEditModal = (item: CompanyTransactionItem) => {
    if (item.source_sale_id) {
      message.info('Edit the sale instead to change this transaction.');
      return;
    }
    setEditingItem(item);
    editForm.setFieldsValue({
      transaction_date: item.transaction_date ? dayjs(item.transaction_date) : dayjs(),
      amount: item.amount,
      data_type: item.data_type,
      product_id: item.product_id,
      expense_category: item.expense_category,
      expense_type: item.expense_type,
    });
    setEditModalOpen(true);
  };

  const handleEditSubmit = async () => {
    if (!editingItem) return;
    try {
      const values = await editForm.validateFields();
      setSubmitting(true);
      const updated = await updateCompanyTransaction(cid, editingItem.id, {
        transaction_date: typeof values.transaction_date === 'string' ? values.transaction_date : values.transaction_date?.format?.('YYYY-MM-DD'),
        amount: values.amount,
        product_id: values.product_id,
        expense_category: values.expense_category,
        expense_type: values.expense_type,
        data_type: values.data_type,
      });
      if (updated) {
        message.success('Transaction updated');
        setEditModalOpen(false);
        setEditingItem(null);
        loadTransactions();
      } else {
        message.error('Failed to update transaction');
      }
    } catch (e) {
      if (e && typeof e === 'object' && 'errorFields' in e) return;
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = (item: CompanyTransactionItem) => {
    if (item.source_sale_id) {
      message.info('Delete the sale instead to remove this transaction.');
      return;
    }
    Modal.confirm({
      title: 'Delete transaction?',
      content: `Remove ${formatCurrency(item.amount)} from ${item.transaction_type}?`,
      onOk: async () => {
        const ok = await deleteCompanyTransaction(cid, item.id);
        if (ok) {
          message.success('Transaction deleted');
          loadTransactions();
        } else {
          message.error('Failed to delete');
        }
      },
    });
  };

  const openSaleModal = () => {
    saleForm.setFieldsValue({
      transaction_date: dayjs(),
      quantity: 1,
      unit_price: 0,
      cac_per_unit: 0,
      cogs_per_unit: 0,
      product_id: undefined,
      product_name: '',
    });
    setSaleModalOpen(true);
  };

  const handleSaleSubmit = async () => {
    try {
      const values = await saleForm.validateFields();
      setSubmitting(true);
      const result = await createCompanySale(cid, {
        transaction_date: typeof values.transaction_date === 'string' ? values.transaction_date : values.transaction_date?.format?.('YYYY-MM-DD') ?? dayjs().format('YYYY-MM-DD'),
        quantity: values.quantity,
        unit_price: values.unit_price,
        cac_per_unit: values.cac_per_unit ?? 0,
        cogs_per_unit: values.cogs_per_unit ?? 0,
        product_id: values.product_id,
        product_name: values.product_name,
      });
      if (result) {
        message.success('Sale added (revenue + CAC + COGS created)');
        setSaleModalOpen(false);
        saleForm.resetFields();
        loadTransactions();
      } else {
        message.error('Failed to add sale');
      }
    } catch (e) {
      if (e && typeof e === 'object' && 'errorFields' in e) return;
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteSale = (item: CompanySaleItem) => {
    Modal.confirm({
      title: 'Delete sale?',
      content: `This will remove the sale and its revenue, CAC, and COGS entries.`,
      onOk: async () => {
        const ok = await deleteCompanySale(cid, item.id);
        if (ok) {
          message.success('Sale deleted');
          loadTransactions();
        } else {
          message.error('Failed to delete');
        }
      },
    });
  };

  const formatCurrency = (n: number) =>
    new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(n);

  const baseTransactionColumns: ColumnsType<CompanyTransactionItem> = [
    { title: 'Date', dataIndex: 'transaction_date', key: 'transaction_date', width: 110 },
    { title: 'Amount', dataIndex: 'amount', key: 'amount', render: (v: number) => formatCurrency(v) },
    { title: 'Product', dataIndex: 'product_id', key: 'product_id', render: (_, r) => r.product_id ? (products.find(p => p.id === r.product_id)?.name ?? r.product_id) : '—' },
    { title: 'Category', dataIndex: 'expense_category', key: 'expense_category', width: 100 },
    { title: 'Type', dataIndex: 'expense_type', key: 'expense_type', width: 90 },
    { title: 'Data', dataIndex: 'data_type', key: 'data_type', width: 80 },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      render: (_, record) => (
        <Space>
          {!record.source_sale_id && (
            <>
              <Button type="link" size="small" icon={<EditOutlined />} onClick={() => openEditModal(record)}>Edit</Button>
              <Button type="link" size="small" danger icon={<DeleteOutlined />} onClick={() => handleDelete(record)}>Delete</Button>
            </>
          )}
          {record.source_sale_id && <Text type="secondary">From sale</Text>}
        </Space>
      ),
    },
  ];
  const revenueColumns = baseTransactionColumns.filter(c => c.key !== 'expense_category' && c.key !== 'expense_type');
  const expenseColumns = baseTransactionColumns.filter(c => c.key !== 'product_id');
  const transactionColumns = baseTransactionColumns; // alias for compatibility

  if (!enabled) {
    return (
      <div style={{ padding: 24 }}>
        <Alert type="info" message="Local Bridge API not configured" description="Ensure the backend is running and REACT_APP_API_URL is configured." />
      </div>
    );
  }

  if (!cid) {
    return (
      <div style={{ padding: 24 }}>
        <Alert type="warning" message="No company selected" description={<Link to="/local-bridge/companies">Select a company</Link>} />
      </div>
    );
  }

  return (
    <div style={{ padding: 24, maxWidth: 1100 }}>
      <div style={{ marginBottom: 24, display: 'flex', alignItems: 'center', gap: 16 }}>
        <Link to="/local-bridge/companies">
          <Button icon={<ArrowLeftOutlined />}>Companies</Button>
        </Link>
      </div>

      <h1 style={{ marginBottom: 8 }}>
        Company Transactions
        {company && <Text type="secondary" style={{ marginLeft: 12, fontWeight: 400 }}>— {company.name}</Text>}
      </h1>
      <p style={{ color: '#6b7280', marginBottom: 24 }}>
        Dated line items. Aggregator sums by period. Sales create revenue + CAC + COGS entries.
      </p>

      <Card>
        <div style={{ marginBottom: 16, display: 'flex', gap: 16, alignItems: 'center' }}>
          <span>Period filter:</span>
          <Input
            placeholder="e.g. 2024-01 or 2024-Q1"
            value={periodFilter}
            onChange={(e) => setPeriodFilter(e.target.value)}
            style={{ width: 140 }}
          />
        </div>
        <Tabs
          activeKey={activeTab}
          onChange={(k) => setActiveTab(k as TabKey)}
          items={[
            {
              key: 'revenue',
              label: 'Revenue',
              children: (
                <>
                  <div style={{ marginBottom: 16 }}>
                    <Button type="primary" icon={<PlusOutlined />} onClick={openAddModal}>Add revenue</Button>
                  </div>
                  {loading ? <Spin /> : revenueItems.length === 0 ? (
                    <p style={{ color: '#6b7280' }}>No revenue entries. Add one above.</p>
                  ) : (
                    <Table
                      rowKey="id"
                      columns={revenueColumns}
                      dataSource={revenueItems}
                      pagination={false}
                      size="middle"
                      summary={() => (
                        <Table.Summary fixed>
                          <Table.Summary.Row>
                            <Table.Summary.Cell index={0}>Total</Table.Summary.Cell>
                            <Table.Summary.Cell index={1}>{formatCurrency(revenueItems.reduce((s, i) => s + i.amount, 0))}</Table.Summary.Cell>
                          </Table.Summary.Row>
                        </Table.Summary>
                      )}
                    />
                  )}
                </>
              ),
            },
            {
              key: 'expense',
              label: 'Expense',
              children: (
                <>
                  <div style={{ marginBottom: 16 }}>
                    <Button type="primary" icon={<PlusOutlined />} onClick={openAddModal}>Add expense</Button>
                  </div>
                  {loading ? <Spin /> : expenseItems.length === 0 ? (
                    <p style={{ color: '#6b7280' }}>No expense entries. Add one above.</p>
                  ) : (
                    <Table
                      rowKey="id"
                      columns={expenseColumns}
                      dataSource={expenseItems}
                      pagination={false}
                      size="middle"
                      summary={() => (
                        <Table.Summary fixed>
                          <Table.Summary.Row>
                            <Table.Summary.Cell index={0}>Total</Table.Summary.Cell>
                            <Table.Summary.Cell index={1}>{formatCurrency(expenseItems.reduce((s, i) => s + i.amount, 0))}</Table.Summary.Cell>
                          </Table.Summary.Row>
                        </Table.Summary>
                      )}
                    />
                  )}
                </>
              ),
            },
            {
              key: 'sales',
              label: 'Sales',
              children: (
                <>
                  <p style={{ color: '#6b7280', marginBottom: 16 }}>
                    Add a sale to create revenue + CAC expense + COGS expense entries automatically.
                  </p>
                  <div style={{ marginBottom: 16 }}>
                    <Button type="primary" icon={<PlusOutlined />} onClick={openSaleModal}>Add sale</Button>
                  </div>
                  {loading ? <Spin /> : salesItems.length === 0 ? (
                    <p style={{ color: '#6b7280' }}>No sales. Add one above.</p>
                  ) : (
                    <Table
                      rowKey="id"
                      columns={[
                        { title: 'Date', dataIndex: 'transaction_date', key: 'transaction_date', width: 110 },
                        { title: 'Product', dataIndex: 'product_name', key: 'product_name', render: (v: string) => v || '—' },
                        { title: 'Qty', dataIndex: 'quantity', key: 'quantity', width: 70 },
                        { title: 'Price', dataIndex: 'unit_price', key: 'unit_price', render: (v: number) => formatCurrency(v) },
                        { title: 'Revenue', dataIndex: 'total_revenue', key: 'total_revenue', render: (v: number) => formatCurrency(v) },
                        { title: 'CAC', dataIndex: 'total_cac', key: 'total_cac', render: (v: number) => formatCurrency(v) },
                        { title: 'COGS', dataIndex: 'total_cogs', key: 'total_cogs', render: (v: number) => formatCurrency(v) },
                        {
                          title: 'Actions',
                          key: 'actions',
                          width: 80,
                          render: (_, record) => (
                            <Button type="link" size="small" danger icon={<DeleteOutlined />} onClick={() => handleDeleteSale(record)}>Delete</Button>
                          ),
                        },
                      ]}
                      dataSource={salesItems}
                      pagination={false}
                      size="middle"
                      summary={() => (
                        <Table.Summary fixed>
                          <Table.Summary.Row>
                            <Table.Summary.Cell index={0}>Total</Table.Summary.Cell>
                            <Table.Summary.Cell index={4}>{formatCurrency(salesItems.reduce((s, i) => s + i.total_revenue, 0))}</Table.Summary.Cell>
                            <Table.Summary.Cell index={5}>{formatCurrency(salesItems.reduce((s, i) => s + i.total_cac, 0))}</Table.Summary.Cell>
                            <Table.Summary.Cell index={6}>{formatCurrency(salesItems.reduce((s, i) => s + i.total_cogs, 0))}</Table.Summary.Cell>
                          </Table.Summary.Row>
                        </Table.Summary>
                      )}
                    />
                  )}
                </>
              ),
            },
          ]}
        />
      </Card>

      <Modal title={activeTab === 'revenue' ? 'Add revenue' : 'Add expense'} open={addModalOpen} onCancel={() => setAddModalOpen(false)} onOk={handleAddSubmit} confirmLoading={submitting} destroyOnClose>
        <Form form={addForm} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item name="transaction_date" label="Date" rules={[{ required: true }]}>
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="amount" label="Amount" rules={[{ required: true }]}>
            <InputNumber min={0} step={100} style={{ width: '100%' }} prefix="$" />
          </Form.Item>
          {activeTab === 'revenue' && (
            <Form.Item name="product_id" label="Product (optional)">
              <Select allowClear placeholder="Select product" options={products.map(p => ({ value: p.id, label: p.name }))} />
            </Form.Item>
          )}
          {activeTab === 'expense' && (
            <>
              <Form.Item name="expense_category" label="Category">
                <Select allowClear placeholder="Select category" options={EXPENSE_CATEGORIES} />
              </Form.Item>
              <Form.Item name="expense_type" label="Type">
                <Select allowClear placeholder="Select type" options={EXPENSE_TYPES} />
              </Form.Item>
            </>
          )}
          <Form.Item name="data_type" label="Data type" initialValue="actual">
            <Select options={[{ value: 'actual', label: 'Actual' }, { value: 'projected', label: 'Projected' }]} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal title="Edit transaction" open={editModalOpen} onCancel={() => { setEditModalOpen(false); setEditingItem(null); }} onOk={handleEditSubmit} confirmLoading={submitting} destroyOnClose>
        <Form form={editForm} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item name="transaction_date" label="Date" rules={[{ required: true }]}>
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="amount" label="Amount" rules={[{ required: true }]}>
            <InputNumber min={0} step={100} style={{ width: '100%' }} prefix="$" />
          </Form.Item>
          {editingItem?.transaction_type === 'revenue' && (
            <Form.Item name="product_id" label="Product (optional)">
              <Select allowClear placeholder="Select product" options={products.map(p => ({ value: p.id, label: p.name }))} />
            </Form.Item>
          )}
          {editingItem?.transaction_type === 'expense' && (
            <>
              <Form.Item name="expense_category" label="Category">
                <Select allowClear options={EXPENSE_CATEGORIES} />
              </Form.Item>
              <Form.Item name="expense_type" label="Type">
                <Select allowClear options={EXPENSE_TYPES} />
              </Form.Item>
            </>
          )}
          <Form.Item name="data_type" label="Data type">
            <Select options={[{ value: 'actual', label: 'Actual' }, { value: 'projected', label: 'Projected' }]} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal title="Add sale" open={saleModalOpen} onCancel={() => setSaleModalOpen(false)} onOk={handleSaleSubmit} confirmLoading={submitting} destroyOnClose width={480}>
        <Form form={saleForm} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item name="transaction_date" label="Date" rules={[{ required: true }]}>
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="product_id" label="Product (optional)">
            <Select allowClear placeholder="Select product" options={products.map(p => ({ value: p.id, label: p.name }))} />
          </Form.Item>
          <Form.Item name="product_name" label="Product name (if no product selected)">
            <Input placeholder="e.g. Widget A" />
          </Form.Item>
          <Form.Item name="quantity" label="Quantity" rules={[{ required: true }]}>
            <InputNumber min={0} step={1} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="unit_price" label="Price per unit" rules={[{ required: true }]}>
            <InputNumber min={0} step={0.01} style={{ width: '100%' }} prefix="$" />
          </Form.Item>
          <Form.Item name="cac_per_unit" label="CAC per unit">
            <InputNumber min={0} step={0.01} style={{ width: '100%' }} prefix="$" />
          </Form.Item>
          <Form.Item name="cogs_per_unit" label="COGS per unit">
            <InputNumber min={0} step={0.01} style={{ width: '100%' }} prefix="$" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default LocalBridgeCompanyTransactionsPage;
